import { useLearnEnabled } from '@/lib/learnFeature';
import MobileLinkPrompt from '@/components/MobileLinkPrompt';
import ArtistTaxonomyFields from '@/components/ArtistTaxonomyFields';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import VideoSubmissionForm from '@/components/VideoSubmissionForm';
import PolicyConsentChecklist, { emptyPolicyConsent, hasAcceptedAllPolicies } from '@/components/PolicyConsentChecklist';
import { useArtistAuth } from '@/context/ArtistAuthContext';
import { artistProfilePath } from '@/lib/artistSlug';
import { hasLocalPolicyAcceptance, markLocalPolicyAcceptance, withAcceptedPolicyFields } from '@/lib/policyAcceptance';
import {
  artistCategoryOptions as categoryOptions,
  getKnownArtCategories,
  getKnownArtSubcategories,
  getOtherArtCategories,
  getOtherArtSubcategories,
  getSubcategoryOptionsForCategories,
  joinArtValues,
  splitArtValues,
} from '@/lib/artistCategories';
import { analyzeArtistProfile, analyzeArtistUploadQuality, buildArtistBioSuggestions, profileScoreBadgeClass, profileScoreBarClass } from '@/lib/profileCoach';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;
const MAX_VIDEOS = 2;
const MAX_PORTFOLIO_IMAGES = 3;
const MAX_PORTFOLIO_VIDEO_LINKS = 2;
const ARTIST_PROFILE_DRAFT_KEY_PREFIX = 'kalamanch_artist_profile_draft';
const blankSlots = (count) => Array.from({ length: count }, () => '');
const withFixedSlots = (values, count) => {
  const nextValues = Array.isArray(values) ? values : [];
  return [...nextValues, ...blankSlots(count)].slice(0, count);
};
const readToggleValue = (value, defaultValue = true) => {
  if (value === undefined || value === null) return defaultValue;
  if (typeof value === 'string') return value.toLowerCase() !== 'false';
  return Boolean(value);
};

const seasonalAvailabilityOptions = [
  'Holi',
  'Navratri / Garba',
  'Diwali',
  'Independence Day',
  'Republic Day',
  'Durga Puja',
  'Ganesh Utsav',
  'Janmashtami',
  'Eid / Mushaira',
  'Christmas',
  'Wedding / Sangeet',
  'College Fest',
  'Corporate Event',
  'School Annual Day',
  'Society Program',
  'Heritage / Folk Festival',
  'Open Mic',
  'Devotional Program',
];

const hasText = (value) => Boolean(String(value || '').trim());
const projectStatusLabel = {
  artist_accepted: 'Interest accepted',
  artist_declined: 'Declined',
  in_review: 'Under poster review',
  poster_accepted: 'Selected',
  poster_rejected: 'Not selected',
  pending_review: 'Admin review',
  published: 'Published',
  admin_rejected: 'Needs changes',
  closed: 'Closed',
};

const checklistItemClass = (isComplete) =>
  `flex items-center justify-between gap-3 rounded-2xl border px-4 py-3 text-sm ${
    isComplete
      ? 'border-green-100 bg-green-50 text-green-800'
      : 'border-orange-100 bg-orange-50 text-orange-800'
  }`;

const PasswordToggleButton = ({ visible, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    className="absolute inset-y-0 right-3 flex items-center text-gray-500 hover:text-gray-700"
    aria-label={visible ? 'Hide password' : 'Show password'}
    title={visible ? 'Hide password' : 'Show password'}
  >
    {visible ? (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M3 3l18 18" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M10.58 10.58A2 2 0 0012 14a2 2 0 001.42-.58" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M9.88 5.09A10.94 10.94 0 0112 4.91c5.05 0 8.27 4.14 9 5.09a13.16 13.16 0 01-3.06 3.37M6.1 6.1A13.23 13.23 0 003 12c.73.95 3.95 5.09 9 5.09a10.9 10.9 0 004.11-.79" />
      </svg>
    ) : (
      <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" d="M2.46 12C3.73 7.94 7.52 5 12 5c4.48 0 8.27 2.94 9.54 7-1.27 4.06-5.06 7-9.54 7-4.48 0-8.27-2.94-9.54-7z" />
        <circle cx="12" cy="12" r="3" strokeWidth="1.8" />
      </svg>
    )}
  </button>
);

const MyAccount = () => {
  const learnEnabled = useLearnEnabled();
  const { artist, setArtist, updateArtist, logout, changePassword } = useArtistAuth();
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    category: '',
    category_other: '',
    subcategory: '',
    subcategory_other: '',
    city: '',
    instagram: '',
    facebook: '',
    spotify: '',
    youtube: '',
    voice_intro_url: '',
    smart_fit: '',
    bio: '',
    native_bio: '',
    native_language: '',
    seasonal_availability: [],
    image_url: '',
    portfolio_image_urls: blankSlots(MAX_PORTFOLIO_IMAGES),
    portfolio_video_urls: blankSlots(MAX_PORTFOLIO_VIDEO_LINKS),
    allow_collaborator_enquiries: true,
    allow_artist_requests: true,
    latitude: '',
    longitude: '',
  });
  const [videos, setVideos] = useState([]);
  const [loadingVideos, setLoadingVideos] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [translationLanguage, setTranslationLanguage] = useState('Hindi');
  const [translatingBio, setTranslatingBio] = useState(false);
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [adminNoteCount, setAdminNoteCount] = useState(0);
  const [projectSummary, setProjectSummary] = useState({ projects: [], responses: [] });
  const [auditionInvites, setAuditionInvites] = useState([]);
  const [learnEnrollments, setLearnEnrollments] = useState([]);
  const [learnEnrollmentsLoading, setLearnEnrollmentsLoading] = useState(true);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [passwordVisibility, setPasswordVisibility] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [policyConsent, setPolicyConsent] = useState(emptyPolicyConsent);
  const [policySaving, setPolicySaving] = useState(false);
  const [policyError, setPolicyError] = useState('');

  const artistDraftStorageKey = artist?.id ? `${ARTIST_PROFILE_DRAFT_KEY_PREFIX}:${artist.id}` : '';

  useEffect(() => {
    if (!artist) return;
    const categoryValue = joinArtValues(getKnownArtCategories(artist.category || ''));
    const otherCategoryValue = joinArtValues(getOtherArtCategories(artist.category || ''));
    const subcategoryValue = joinArtValues(getKnownArtSubcategories(splitArtValues(categoryValue), artist.subcategory || ''));
    const otherSubcategoryValue = joinArtValues(getOtherArtSubcategories(splitArtValues(categoryValue), artist.subcategory || ''));
    let nextFormData = {
      name: artist.name || '',
      mobile: artist.mobile || '',
      email: artist.email || '',
      category: categoryValue,
      category_other: otherCategoryValue,
      subcategory: subcategoryValue,
      subcategory_other: otherSubcategoryValue,
      city: artist.city || '',
      instagram: artist.instagram || '',
      facebook: artist.facebook || '',
      spotify: artist.spotify || '',
      youtube: artist.youtube || '',
      voice_intro_url: artist.voice_intro_url || '',
      smart_fit: artist.smart_fit || '',
      bio: artist.bio || '',
      native_bio: artist.native_bio || '',
      native_language: artist.native_language || '',
      seasonal_availability: Array.isArray(artist.seasonal_availability) ? artist.seasonal_availability : [],
      image_url: artist.image_url || '',
      portfolio_image_urls: withFixedSlots(artist.portfolio_image_urls, MAX_PORTFOLIO_IMAGES),
      portfolio_video_urls: withFixedSlots(artist.portfolio_video_urls, MAX_PORTFOLIO_VIDEO_LINKS),
      allow_collaborator_enquiries: readToggleValue(artist.allow_collaborator_enquiries, true),
      allow_artist_requests: readToggleValue(artist.allow_artist_requests, true),
      latitude: artist.latitude ?? '',
      longitude: artist.longitude ?? '',
    };
    try {
      const rawDraft = artist?.id ? localStorage.getItem(`${ARTIST_PROFILE_DRAFT_KEY_PREFIX}:${artist.id}`) : '';
      if (rawDraft) {
        const draft = JSON.parse(rawDraft);
        if (draft?.formData) {
          nextFormData = {
            ...nextFormData,
            ...draft.formData,
            portfolio_image_urls: withFixedSlots(draft.formData.portfolio_image_urls, MAX_PORTFOLIO_IMAGES),
            portfolio_video_urls: withFixedSlots(draft.formData.portfolio_video_urls, MAX_PORTFOLIO_VIDEO_LINKS),
            seasonal_availability: Array.isArray(draft.formData.seasonal_availability) ? draft.formData.seasonal_availability : [],
          };
          setMessage('Recovered your unsaved profile draft. Review it, then save when ready.');
        }
      }
    } catch (error) {
      if (artist?.id) {
        localStorage.removeItem(`${ARTIST_PROFILE_DRAFT_KEY_PREFIX}:${artist.id}`);
      }
    }
    setFormData(nextFormData);
  }, [artist]);

  useEffect(() => {
    if (!artist || !artistDraftStorageKey) return;
    try {
      localStorage.setItem(
        artistDraftStorageKey,
        JSON.stringify({
          formData,
          savedAt: new Date().toISOString(),
        })
      );
    } catch (error) {
      // ignore draft persistence errors
    }
  }, [artist, artistDraftStorageKey, formData]);

  useEffect(() => {
    if (!artist?.id) return;
    let mounted = true;
    const fetchVideos = async () => {
      try {
        const response = await axios.get(`${API}/videos/artist/${artist.id}`);
        if (mounted) setVideos(response.data.videos || []);
      } catch (fetchError) {
        if (mounted) setVideos([]);
      } finally {
        if (mounted) setLoadingVideos(false);
      }
    };
    fetchVideos();
    return () => {
      mounted = false;
    };
  }, [artist?.id]);

  const fetchAuditionInvites = async () => {
    try {
      const response = await axios.get(`${API}/artist-auth/live-auditions/invites`, { withCredentials: true });
      setAuditionInvites(response.data.invites || []);
    } catch (fetchError) {
      setAuditionInvites([]);
    }
  };

  useEffect(() => {
    if (!artist?.id) return;
    fetchAuditionInvites();
  }, [artist?.id]);

  useEffect(() => {
    if (!artist?.id) return;
    let mounted = true;
    const fetchLearnEnrollments = async () => {
      if (!learnEnabled) { setLearnEnrollmentsLoading(false); return; }
      setLearnEnrollmentsLoading(true);
      try {
        const response = await axios.get(`${API}/learn/me/enrollments`, { withCredentials: true });
        if (mounted) setLearnEnrollments(response.data?.enrollments || []);
      } catch (_requestError) {
        if (mounted) setLearnEnrollments([]);
      } finally {
        if (mounted) setLearnEnrollmentsLoading(false);
      }
    };
    fetchLearnEnrollments();
    return () => {
      mounted = false;
    };
  }, [artist?.id, learnEnabled]);

  useEffect(() => {
    if (!artist?.id) return;
    let mounted = true;
    const fetchProjects = async () => {
      try {
        const response = await axios.get(`${API}/projects/mine`, { withCredentials: true });
        if (mounted) {
          setProjectSummary({
            projects: response.data.projects || [],
            responses: response.data.responses || [],
          });
        }
      } catch (fetchError) {
        if (mounted) setProjectSummary({ projects: [], responses: [] });
      }
    };
    fetchProjects();
    return () => {
      mounted = false;
    };
  }, [artist?.id]);

  useEffect(() => {
    if (!artist?.id) return;
    let mounted = true;
    const fetchAdminNotes = async () => {
      try {
        const response = await axios.get(`${API}/artist-auth/enquiries`, { withCredentials: true });
        const adminThreads = (response.data.conversations || []).filter(
          (conversation) => conversation.collaborator_id === 'admin' || conversation.status === 'admin_note'
        );
        if (mounted) setAdminNoteCount(adminThreads.length);
      } catch (fetchError) {
        if (mounted) setAdminNoteCount(0);
      }
    };
    fetchAdminNotes();
    return () => {
      mounted = false;
    };
  }, [artist?.id]);

  const selectedCategories = splitArtValues(formData.category);
  const selectedSubcategories = splitArtValues(formData.subcategory);
  const availableSubcategories = getSubcategoryOptionsForCategories(selectedCategories);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => {
      return { ...prev, [name]: value };
    });
  };

  const toggleCategory = (category) => {
    setFormData((prev) => {
      const currentCategories = splitArtValues(prev.category);
      const nextCategories = currentCategories.includes(category)
        ? currentCategories.filter((item) => item !== category)
        : [...currentCategories, category];
      const allowedSubcategories = new Set(getSubcategoryOptionsForCategories(nextCategories));
      const nextSubcategories = splitArtValues(prev.subcategory).filter((item) => allowedSubcategories.has(item));
      return {
        ...prev,
        category: joinArtValues(nextCategories),
        subcategory: joinArtValues(nextSubcategories),
      };
    });
  };

  const toggleSubcategory = (subcategory) => {
    setFormData((prev) => {
      const currentSubcategories = splitArtValues(prev.subcategory);
      const nextSubcategories = currentSubcategories.includes(subcategory)
        ? currentSubcategories.filter((item) => item !== subcategory)
        : [...currentSubcategories, subcategory];
      return { ...prev, subcategory: joinArtValues(nextSubcategories) };
    });
  };

  const handleSlotChange = (field, index, value) => {
    setFormData((prev) => {
      const nextValues = [...withFixedSlots(prev[field], field === 'portfolio_image_urls' ? MAX_PORTFOLIO_IMAGES : MAX_PORTFOLIO_VIDEO_LINKS)];
      nextValues[index] = value;
      return { ...prev, [field]: nextValues };
    });
  };

  const toggleSeasonalAvailability = (option) => {
    setFormData((prev) => {
      const current = Array.isArray(prev.seasonal_availability) ? prev.seasonal_availability : [];
      const exists = current.includes(option);
      return {
        ...prev,
        seasonal_availability: exists ? current.filter((item) => item !== option) : [...current, option],
      };
    });
  };

  const handleImageUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    setError('');
    setMessage('');
    try {
      const payload = new FormData();
      payload.append('file', file);
      const response = await axios.post(`${API}/uploads/profile-image`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      setFormData((prev) => ({ ...prev, image_url: response.data.image_url }));
      setMessage('Profile image uploaded. Save changes so admin can review it.');
    } catch (uploadError) {
      setError(uploadError.response?.data?.detail || 'Failed to upload image.');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const handlePortfolioImageUpload = async (event, index) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setUploadingImage(true);
    setError('');
    setMessage('');
    try {
      const payload = new FormData();
      payload.append('file', file);
      const response = await axios.post(`${API}/uploads/profile-image`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' },
        withCredentials: true,
      });
      handleSlotChange('portfolio_image_urls', index, response.data.image_url || '');
      setMessage('Portfolio photo uploaded. Save changes so admin can review it.');
    } catch (uploadError) {
      setError(uploadError.response?.data?.detail || 'Failed to upload image.');
    } finally {
      setUploadingImage(false);
      event.target.value = '';
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);
    setMessage('');
    setError('');
    try {
      if (!joinArtValues([...splitArtValues(formData.category), ...splitArtValues(formData.category_other)])) {
        throw new Error('Please select or type at least one art category.');
      }
      if (!formData.city.trim()) {
        throw new Error('City is required.');
      }
      if (String(formData.mobile || '').includes(',')) {
        throw new Error('Please enter only one mobile number in the mobile field.');
      }
      const hasPortfolioLink = [formData.instagram, formData.facebook, formData.spotify, formData.youtube].some((value) => String(value || '').trim());
      if (!hasPortfolioLink) {
        throw new Error('Please add at least one social or portfolio link for verification.');
      }
      await updateArtist({
        ...formData,
        category: joinArtValues([...splitArtValues(formData.category), ...splitArtValues(formData.category_other)]),
        subcategory: joinArtValues([...splitArtValues(formData.subcategory), ...splitArtValues(formData.subcategory_other)]),
        portfolio_video_urls: formData.portfolio_video_urls.map((value) => value.trim()).filter(Boolean),
        seasonal_availability: (formData.seasonal_availability || []).map((value) => value.trim()).filter(Boolean),
        latitude: formData.latitude === '' ? null : Number(formData.latitude),
        longitude: formData.longitude === '' ? null : Number(formData.longitude),
      });
      if (artistDraftStorageKey) {
        localStorage.removeItem(artistDraftStorageKey);
      }
      setMessage('Your profile has been updated. Admin verification is required before public listing or portfolio changes appear.');
    } catch (saveError) {
      setError(saveError.response?.data?.detail || saveError.message || 'Failed to save your profile.');
    } finally {
      setSaving(false);
    }
  };

  const handleTranslateNativeBio = async () => {
    const sourceText = formData.bio.trim();
    if (sourceText.length < 10) {
      setError('Add an English bio first, then translate it.');
      return;
    }
    setTranslatingBio(true);
    setError('');
    setMessage('');
    try {
      const response = await axios.post(
        `${API}/ai/translate-native-bio`,
        {
          sourceText,
          targetLanguage: translationLanguage,
          artForm: formData.subcategory || formData.category || 'Artist',
        },
        { withCredentials: true }
      );
      setFormData((prev) => ({
        ...prev,
        native_bio: response.data?.result || '',
        native_language: translationLanguage,
      }));
      setMessage('Native language bio generated. Please review it, then save changes.');
    } catch (translateError) {
      setError(translateError.response?.data?.detail || 'Unable to translate bio right now.');
    } finally {
      setTranslatingBio(false);
    }
  };

  const captureCurrentLocation = () => {
    setError('');
    setMessage('');
    if (!navigator.geolocation) {
      setError('Location is not available on this browser.');
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setFormData((prev) => ({
          ...prev,
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6),
        }));
        setMessage('Location captured. Save changes to enable better Near Me discovery.');
      },
      () => {
        setError('Could not capture location. Please allow location permission or keep city-only discovery.');
      },
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
    );
  };

  const handleVideoAdded = (newVideo) => {
    setVideos((prev) => [newVideo, ...prev]);
    setShowVideoForm(false);
  };

  const handlePasswordChange = async (event) => {
    event.preventDefault();
    setPasswordMessage('');
    setPasswordError('');

    if (passwordData.newPassword.trim() !== passwordData.confirmPassword.trim()) {
      setPasswordError('New password and confirmation do not match.');
      return;
    }
    if (passwordData.newPassword.trim().length < 8) {
      setPasswordError('New password must be at least 8 characters.');
      return;
    }

    setPasswordSaving(true);
    try {
      const response = await changePassword({
        currentPassword: passwordData.currentPassword,
        newPassword: passwordData.newPassword,
      });
      setPasswordMessage(response?.message || 'Password changed successfully. A confirmation email has been sent.');
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (saveError) {
      setPasswordError(saveError.response?.data?.detail || saveError.message || 'Failed to change password.');
    } finally {
      setPasswordSaving(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setPasswordVisibility((prev) => ({ ...prev, [field]: !prev[field] }));
  };

  const updatePolicyConsent = (key, value) => {
    setPolicyConsent((prev) => ({ ...prev, [key]: value }));
    setPolicyError('');
  };

  if (!artist) return null;
  const canAddMoreVideos = videos.length < MAX_VIDEOS;
  const profileCoach = analyzeArtistProfile({ ...artist, ...formData }, { videoCount: videos.length });
  const uploadCoach = analyzeArtistUploadQuality({ ...artist, ...formData }, { videoCount: videos.length });
  const artistBioSuggestions = buildArtistBioSuggestions({ ...artist, ...formData });
  const checklist = [
    ...profileCoach.checklist,
    { label: 'Admin verified', complete: Boolean(artist.is_verified) },
  ];
  const completedChecklistItems = checklist.filter((item) => item.complete).length;
  const profileStatus = artist.is_verified
    ? artist.is_featured
      ? 'Verified and featured'
      : 'Verified'
    : adminNoteCount > 0
      ? 'Changes requested'
      : completedChecklistItems >= 4
        ? 'Under review'
        : 'Unverified';
  const statusClasses = artist.is_verified
    ? 'border-green-200 bg-green-50 text-green-800'
    : adminNoteCount > 0
      ? 'border-purple-200 bg-purple-50 text-purple-800'
      : 'border-orange-200 bg-orange-50 text-orange-800';
  const publicProfileUrl = artistProfilePath(artist);
  const absolutePublicProfileUrl = `${window.location.origin}${publicProfileUrl}`;
  const publicProfileQrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=132x132&margin=10&data=${encodeURIComponent(absolutePublicProfileUrl)}`;
  const isOpenForWork = Boolean(formData.allow_collaborator_enquiries || formData.allow_artist_requests);
  const publicContactSummary = [
    formData.allow_collaborator_enquiries ? 'Collaborators can contact you' : 'Collaborator enquiries are turned off',
    formData.allow_artist_requests ? 'Artists can view contact after login' : 'Artist-to-artist contact is turned off',
  ];
  const activeProjectResponses = projectSummary.responses.filter((response) => response.status !== 'artist_declined');
  const needsPolicyAcceptance = !hasLocalPolicyAcceptance('artist', artist) && !hasAcceptedAllPolicies({
    privacy: artist.privacy_policy_accepted,
    terms: artist.terms_accepted,
    trust: artist.trust_safety_accepted,
  });

  const handlePolicyAcceptance = async () => {
    if (!hasAcceptedAllPolicies(policyConsent)) {
      setPolicyError('Please read and accept all three policies before continuing.');
      return;
    }
    setPolicySaving(true);
    setPolicyError('');
    try {
      const response = await axios.post(
        `${API}/artist-auth/policy-acceptance`,
        {
          privacy_policy_accepted: policyConsent.privacy,
          terms_accepted: policyConsent.terms,
          trust_safety_accepted: policyConsent.trust,
        },
        { withCredentials: true }
      );
      const acceptedArtist = withAcceptedPolicyFields(response.data);
      markLocalPolicyAcceptance('artist', acceptedArtist);
      setArtist(acceptedArtist);
      setMessage('Policy acceptance saved. Admin verification still controls public publishing.');
    } catch (requestError) {
      setPolicyError(requestError.response?.data?.detail || 'Unable to save policy acceptance.');
    } finally {
      setPolicySaving(false);
    }
  };

  const respondToAuditionInvite = async (invite, action) => {
    try {
      await axios.post(
        `${API}/artist-auth/live-auditions/${invite.audition_id}/invites/${invite.artist_id}/action`,
        { action },
        { withCredentials: true },
      );
      setMessage(action === 'accept' ? 'Live audition invite accepted.' : 'Live audition invite declined.');
      await fetchAuditionInvites();
    } catch (requestError) {
      setError(requestError.response?.data?.detail || 'Unable to update audition invite.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24" data-testid="my-account-page">
      <MobileLinkPrompt account={artist} accountType="artist" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-white rounded-3xl shadow-xl p-5 sm:p-8 mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            <div className="flex min-w-0 items-center gap-4 sm:gap-5">
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden bg-white border border-orange-100 flex items-center justify-center text-3xl sm:text-4xl font-bold text-[#FF6F00] shrink-0">
                {artist.image_url ? <img src={artist.image_url} alt={artist.name} className="w-full h-full object-contain p-1" /> : artist.name?.charAt(0)?.toUpperCase() || 'A'}
              </div>
              <div className="min-w-0">
                <h1 className="mb-2 inline-block max-w-full pr-1 pb-1 text-3xl font-bold leading-tight gradient-text sm:text-4xl">My Account</h1>
                <p className="text-gray-700 text-base sm:text-lg break-words">{artist.name}</p>
                <p className="text-sm text-gray-500 break-words">{artist.category}</p>
                <span className={`mt-2 inline-flex rounded-full border px-3 py-1 text-xs font-bold ${isOpenForWork ? 'border-emerald-200 bg-emerald-50 text-emerald-700' : 'border-gray-200 bg-gray-50 text-gray-500'}`}>
                  {isOpenForWork ? 'Open for Work' : 'Not Open for Work'}
                </span>
              </div>
            </div>
            <div className="grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:w-auto lg:grid-cols-3 xl:grid-cols-4">
              <a href="#edit-profile" className="btn-outline text-center whitespace-nowrap">Edit Profile</a>
              <Link to={publicProfileUrl} className="btn-outline text-center whitespace-nowrap">Open Public Page</Link>
              <button
                type="button"
                className="btn-outline text-center whitespace-nowrap"
                onClick={async () => {
                  if (navigator.share) {
                    await navigator.share({
                      title: `${artist.name} on Kalamanch.art`,
                      text: 'View my Kalamanch digital visiting card',
                      url: absolutePublicProfileUrl,
                    });
                    return;
                  }
                  await navigator.clipboard?.writeText(absolutePublicProfileUrl);
                  setMessage('Digital visiting card link copied.');
                }}
              >
                Share Card
              </button>
              <Link to="/artist/inbox" className="btn-outline text-center whitespace-nowrap">Open Inbox</Link>
              {learnEnabled && <a href="#artist-learn-courses" className="btn-outline text-center whitespace-nowrap">My Courses</a>}
              <Link to="/tools/exhibition-wall" className="btn-outline text-center whitespace-nowrap">Exhibition Wall</Link>
              <Link to="/projects" className="btn-outline text-center whitespace-nowrap">Projects</Link>
              <Link to="/live-auditions" className="btn-outline text-center whitespace-nowrap">Auditions</Link>
              <Link to="/ai-manager" className="btn-outline text-center whitespace-nowrap">AI Manager</Link>
              <button type="button" className="btn-primary whitespace-nowrap" onClick={logout}>Logout</button>
            </div>
          </div>
        </div>

        {needsPolicyAcceptance && (
          <div className="mb-8 rounded-3xl border border-orange-200 bg-white p-5 shadow-lg sm:p-6">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">Account acceptance required</p>
            <h2 className="mt-2 text-2xl font-black text-gray-950">Accept current platform policies</h2>
            <p className="mt-2 text-sm leading-6 text-gray-700">
              This account can be reviewed by admin only after the Privacy Policy, Terms & Conditions, and Trust & Safety rules are accepted. Admin verification still controls public publishing.
            </p>
            <div className="mt-4">
              <PolicyConsentChecklist value={policyConsent} onChange={updatePolicyConsent} />
            </div>
            {policyError && <p className="mt-3 text-sm font-semibold text-red-600">{policyError}</p>}
            <button
              type="button"
              className="btn-primary mt-4"
              onClick={handlePolicyAcceptance}
              disabled={policySaving || !hasAcceptedAllPolicies(policyConsent)}
            >
              {policySaving ? 'Saving...' : 'Accept and Continue'}
            </button>
          </div>
        )}

        {learnEnabled && (<section id="artist-learn-courses" className="mb-8 rounded-3xl border border-purple-100 bg-white p-5 shadow-lg sm:p-8">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div>
              <p className="text-xs font-black uppercase tracking-[0.22em] text-[#4A148C]">AI &amp; Learn Courses</p>
              <h2 className="mt-2 text-2xl font-black text-gray-950 sm:text-3xl">Your enrolled courses</h2>
              <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600">Your artist account also works as your learner account. Continue any course you have enrolled in here.</p>
            </div>
            <a href="https://learn.kalamanch.art/?section=my-plans" className="btn-primary whitespace-nowrap">Open Learn</a>
          </div>

          {learnEnrollmentsLoading ? (
            <div className="mt-5 rounded-2xl bg-purple-50 px-4 py-4 text-sm font-bold text-purple-700">Loading your courses...</div>
          ) : learnEnrollments.length === 0 ? (
            <div className="mt-5 rounded-2xl border border-slate-100 bg-slate-50 px-4 py-4 text-sm font-semibold text-gray-600">
              No enrolled courses yet. Open Learn, complete Level 1, and enroll free when you are ready to continue.
            </div>
          ) : (
            <div className="mt-5 grid gap-4 lg:grid-cols-2">
              {learnEnrollments.map((enrollment) => {
                const completed = Number(enrollment.progress_summary?.completed_lessons || 0);
                const total = Number(enrollment.progress_summary?.total_lessons || 0);
                const percent = total ? Math.min(100, Math.round((completed / total) * 100)) : 0;
                const tier = enrollment.curriculum?.course_tier || 'guide';
                const tierLabel = tier === 'coached' ? 'AI coached' : tier === 'coming_soon' ? 'AI feedback coming soon' : 'Practice Guide';
                return (
                  <article key={enrollment.id || enrollment.learner_curriculum_id} className="rounded-2xl border border-purple-100 bg-gradient-to-br from-purple-50 to-white p-4">
                    <div className="flex flex-wrap items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <span className="inline-flex rounded-full bg-white px-3 py-1 text-xs font-black uppercase tracking-[0.1em] text-[#4A148C] ring-1 ring-purple-100">{tierLabel}</span>
                        <h3 className="mt-3 text-lg font-black text-gray-950">{enrollment.curriculum?.title || 'KalaManch Learn course'}</h3>
                        <p className="mt-1 text-sm font-semibold text-gray-600">{enrollment.curriculum?.art_form_name || ''}</p>
                      </div>
                      <span className="rounded-full bg-white px-3 py-1 text-xs font-black text-emerald-700 ring-1 ring-emerald-100">{percent}% done</span>
                    </div>
                    <div className="mt-4 h-2 overflow-hidden rounded-full bg-white ring-1 ring-purple-100">
                      <div className="h-full rounded-full bg-purple-600" style={{ width: `${percent}%` }} />
                    </div>
                    <p className="mt-3 text-sm font-bold text-gray-700">{completed} of {total} lessons complete</p>
                    <a href="https://learn.kalamanch.art/?section=my-plans" className="btn-outline mt-4 inline-flex !px-4 !py-2 text-sm">Continue course</a>
                  </article>
                );
              })}
            </div>
          )}
        </section>)}

        <div className="mb-8 grid gap-4 md:grid-cols-2">
          <Link to="/projects" className="rounded-3xl border border-orange-100 bg-white p-5 shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-[#FF6F00]">Projects</p>
            <h2 className="mt-2 text-2xl font-black text-gray-950">My project activity</h2>
            <p className="mt-2 text-sm text-gray-600">
              {activeProjectResponses.length} accepted interest(s), {projectSummary.projects.length} posted project(s).
            </p>
          </Link>
          {activeProjectResponses.length > 0 && (
            <div className="rounded-3xl border border-emerald-100 bg-white p-5 shadow-lg">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-emerald-700">Accepted interests</p>
              <div className="mt-3 space-y-2">
                {activeProjectResponses.slice(0, 3).map((response) => (
                  <div key={response.id || `${response.project_id}-${response.artist_id}`} className="rounded-2xl bg-emerald-50 px-4 py-3">
                    <p className="text-sm font-black text-gray-950">{response.project_title || response.project?.title || 'Project'}</p>
                    <p className="mt-1 text-xs font-bold text-emerald-800">{projectStatusLabel[response.status] || response.status}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
          <Link to="/live-auditions" className="rounded-3xl border border-purple-100 bg-white p-5 shadow-lg transition hover:-translate-y-0.5 hover:shadow-xl">
            <p className="text-xs font-black uppercase tracking-[0.22em] text-[#4A148C]">Live Auditions</p>
            <h2 className="mt-2 text-2xl font-black text-gray-950">My audition invites</h2>
            <p className="mt-2 text-sm text-gray-600">{auditionInvites.length} invitation(s) synced from KalaManch Live Auditions.</p>
          </Link>
          {auditionInvites.length > 0 && (
            <div className="rounded-3xl border border-purple-100 bg-white p-5 shadow-lg md:col-span-2">
              <p className="text-xs font-black uppercase tracking-[0.22em] text-[#4A148C]">Audition responses</p>
              <div className="mt-3 grid gap-3 md:grid-cols-2">
                {auditionInvites.slice(0, 4).map((invite) => (
                  <div key={invite.id || `${invite.audition_id}-${invite.artist_id}`} className="rounded-2xl bg-purple-50 px-4 py-3">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-black text-gray-950">{invite.audition_title || invite.audition?.title || 'Live audition'}</p>
                        <p className="mt-1 text-xs font-bold text-[#4A148C]">{(invite.status || 'invited').replaceAll('_', ' ')}</p>
                      </div>
                      {(invite.status || 'invited') === 'invited' && (
                        <div className="flex flex-wrap gap-2">
                          <button type="button" onClick={() => respondToAuditionInvite(invite, 'accept')} className="rounded-full bg-emerald-600 px-4 py-2 text-xs font-bold text-white">Accept</button>
                          <button type="button" onClick={() => respondToAuditionInvite(invite, 'decline')} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-xs font-bold text-slate-700">Decline</button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="bg-white rounded-3xl shadow-lg p-4 sm:p-8 mb-8">
          <p className="text-xs font-semibold tracking-[0.14em] sm:tracking-[0.28em] text-[#FF6F00] uppercase mb-4 break-words">Public Profile View</p>
          <div className="rounded-3xl border border-orange-100 bg-white overflow-hidden">
            <div className="bg-gradient-to-br from-[#B35A12] via-[#925123] to-[#0E8B73] px-3 py-5 sm:px-8 sm:py-8">
              <div className="rounded-3xl bg-white shadow-xl p-4 sm:p-8">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
                  <div className="flex min-w-0 flex-col sm:flex-row sm:items-start gap-4 sm:gap-5">
                    <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full overflow-hidden bg-white border border-orange-100 flex items-center justify-center text-3xl sm:text-4xl font-bold text-[#FF6F00] shrink-0">
                      {artist.image_url ? <img src={artist.image_url} alt={artist.name} className="w-full h-full object-contain p-1" /> : artist.name?.charAt(0)?.toUpperCase() || 'A'}
                    </div>
                    <div className="min-w-0">
                      <h2 className="text-2xl sm:text-4xl font-bold text-gray-900 break-words">{artist.name || 'Artist profile'}</h2>
                      <p className="mt-2 text-lg sm:text-xl font-semibold text-[#FF6F00] break-words">{artist.category || 'Artist profile'}</p>
                      {artist.subcategory && <p className="mt-2 text-gray-600 break-words">{artist.subcategory}</p>}
                      <div className="mt-4 flex flex-wrap gap-2">
                        <span className={`rounded-full border px-3 py-1 text-sm font-semibold ${statusClasses}`}>
                          {profileStatus}
                        </span>
                        {artist.city && <span className="max-w-full break-words rounded-full border border-gray-200 px-3 py-1 text-sm text-gray-600">{artist.city}</span>}
                      </div>
                    </div>
                  </div>
                  <div className="w-full lg:w-auto flex flex-col sm:flex-row lg:flex-col gap-3">
                    <Link to={publicProfileUrl} className="btn-primary text-center">View Public Profile</Link>
                    <a href="#edit-profile" className="btn-outline text-center">Edit Public Details</a>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 sm:gap-6 p-4 sm:p-8">
              <div className="lg:col-span-2 rounded-3xl border border-orange-100 bg-orange-50/40 p-5">
                <h3 className="text-xl font-bold text-gray-900 mb-3">About</h3>
                <p className="text-gray-700 leading-relaxed">{artist.bio || 'Your public about section will appear here once you add your artist story, experience, and style.'}</p>
                {formData.voice_intro_url && (
                  <div className="mt-5 rounded-2xl border border-orange-100 bg-white p-4">
                    <p className="font-bold text-gray-900">Voice Intro</p>
                    <p className="mb-3 text-sm text-gray-600">A short audio intro for pronunciation, voice, and personality.</p>
                    <audio controls preload="none" src={formData.voice_intro_url} className="w-full">
                      <a href={formData.voice_intro_url} target="_blank" rel="noopener noreferrer">Open voice intro</a>
                    </audio>
                  </div>
                )}
              </div>
              <div className="rounded-3xl border border-orange-100 bg-white p-5">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Contact View</h3>
                <div className="space-y-2 text-sm text-gray-700">
                  {publicContactSummary.map((item) => (
                    <p key={item}>{item}</p>
                  ))}
                  <p><span className="font-semibold">Email:</span> {artist.email || 'Not added'}</p>
                </div>
              </div>
              <div className="rounded-3xl border border-orange-100 bg-white p-5">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Portfolio</h3>
                <div className="space-y-2 text-sm text-gray-700">
                  <p><span className="font-semibold">Photos:</span> {(artist.portfolio_image_urls || []).length}</p>
                  <p><span className="font-semibold">Video links:</span> {(artist.portfolio_video_urls || []).length}</p>
                  <p><span className="font-semibold">Instagram:</span> {artist.instagram ? 'Linked' : 'Not linked'}</p>
                  <p><span className="font-semibold">YouTube:</span> {artist.youtube ? 'Linked' : 'Not linked'}</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {adminNoteCount > 0 && !artist.is_verified && (
          <div className="mb-8 rounded-3xl border border-purple-200 bg-purple-50 px-6 py-5 text-purple-900 shadow-sm">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-sm font-semibold uppercase tracking-[0.2em]">Kala Manch Admin</p>
                <h2 className="mt-1 text-2xl font-bold">Profile changes requested</h2>
                <p className="mt-2 text-sm text-purple-800">
                  Admin has sent a profile-review note. Please check your inbox, update your profile, and save changes for review again.
                </p>
              </div>
              <Link to="/artist/inbox" className="btn-primary whitespace-nowrap">Open Admin Note</Link>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 xl:grid-cols-3 gap-8">
          <div className="xl:col-span-2">
            <form id="edit-profile" onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-lg p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-3xl font-bold gradient-text">Edit Profile</h2>
                <span className="text-sm text-gray-500">Logged in as {artist.email || artist.mobile}</span>
              </div>
              <div className="mb-5 rounded-2xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm text-gray-700">
                Keep city, bio, profile image, and at least one portfolio link complete. Admin reviews profiles before they appear publicly.
              </div>
              <div className="mb-5 rounded-2xl border border-emerald-100 bg-emerald-50 px-4 py-3 text-sm text-emerald-900">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                  <div className="min-w-0">
                    <p className="font-bold">Digital Visiting Card</p>
                    <p className="mt-1 break-all">{absolutePublicProfileUrl}</p>
                    <p className="mt-1 text-xs text-emerald-800">This public link is your shareable artist card with profile, portfolio, social links, and booking/contact buttons.</p>
                  </div>
                  <img
                    src={publicProfileQrUrl}
                    alt={`${artist.name} Kalamanch digital visiting card QR code`}
                    className="h-24 w-24 shrink-0 rounded-2xl border border-emerald-100 bg-white p-1 shadow-sm"
                    loading="lazy"
                  />
                </div>
              </div>
              {message && <div className="mb-4 rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-green-700">{message}</div>}
              {error && <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-700">{error}</div>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <input name="name" value={formData.name} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl" required placeholder="Full Name" />
                <input name="mobile" value={formData.mobile} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl" placeholder="Mobile (optional)" />
                <input name="email" type="email" value={formData.email} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl" required placeholder="Email" />
                <input name="city" value={formData.city} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl" required placeholder="City *" />
                <div className="md:col-span-2 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="font-semibold text-gray-900">Near Me discovery</p>
                      <p className="mt-1 text-xs text-gray-600">Capture approximate coordinates so nearby collaborators and artists can find you. Your exact coordinates are not shown publicly.</p>
                    </div>
                    <button type="button" onClick={captureCurrentLocation} className="rounded-full bg-emerald-700 px-4 py-2 text-sm font-bold text-white">
                      Use My Location
                    </button>
                  </div>
                  <div className="mt-3 grid gap-3 sm:grid-cols-2">
                    <input name="latitude" value={formData.latitude} onChange={handleChange} className="w-full px-4 py-3 border border-emerald-200 rounded-xl bg-white" placeholder="Latitude" />
                    <input name="longitude" value={formData.longitude} onChange={handleChange} className="w-full px-4 py-3 border border-emerald-200 rounded-xl bg-white" placeholder="Longitude" />
                  </div>
                </div>
              <ArtistTaxonomyFields value={formData} onChange={setFormData} />
                <input name="instagram" value={formData.instagram} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl" placeholder="Instagram profile URL" />
                <input name="facebook" value={formData.facebook} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl" placeholder="Facebook page URL" />
                <input name="spotify" value={formData.spotify} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl" placeholder="Spotify artist/profile URL" />
                <input name="youtube" value={formData.youtube} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl" placeholder="YouTube profile or channel URL" />
                <input name="voice_intro_url" value={formData.voice_intro_url} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl" placeholder="Voice intro audio URL (10-sec MP3/M4A/WAV)" />
                <input name="smart_fit" value={formData.smart_fit} onChange={handleChange} className="w-full px-4 py-3 border border-gray-300 rounded-xl md:col-span-2" placeholder="Preferred work: weddings, workshops, school events, brand shows" />
                <div className="md:col-span-2 rounded-2xl border border-amber-100 bg-amber-50/60 p-4">
                  <p className="font-semibold text-gray-900">Seasonal discovery availability</p>
                  <p className="mt-1 text-xs text-gray-600">Choose occasions you perform for so organizers can find you during festival, school, society, wedding, and corporate searches.</p>
                  <div className="mt-4 flex flex-wrap gap-2">
                    {seasonalAvailabilityOptions.map((option) => {
                      const selected = (formData.seasonal_availability || []).includes(option);
                      return (
                        <button
                          key={option}
                          type="button"
                          onClick={() => toggleSeasonalAvailability(option)}
                          className={`rounded-full border px-3 py-2 text-xs font-bold transition ${
                            selected
                              ? 'border-[#FF6F00] bg-[#FF6F00] text-white shadow-sm'
                              : 'border-amber-200 bg-white text-amber-900 hover:border-[#FF6F00]'
                          }`}
                        >
                          {option}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="md:col-span-2 rounded-2xl border border-orange-100 bg-orange-50/60 p-4">
                  <p className="font-semibold text-gray-900">Contact preferences</p>
                  <div className="mt-3 space-y-3">
                    <label className="flex items-start gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={Boolean(formData.allow_collaborator_enquiries)}
                        onChange={(event) => setFormData((prev) => ({ ...prev, allow_collaborator_enquiries: event.target.checked }))}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-[#FF6F00] focus:ring-[#FF6F00]"
                      />
                      <span>Allow collaborators to contact me through KalaManch.</span>
                    </label>
                    <label className="flex items-start gap-3 text-sm text-gray-700">
                      <input
                        type="checkbox"
                        checked={Boolean(formData.allow_artist_requests)}
                        onChange={(event) => setFormData((prev) => ({ ...prev, allow_artist_requests: event.target.checked }))}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-[#FF6F00] focus:ring-[#FF6F00]"
                      />
                      <span>Allow other artists to view my contact details after login for collaboration.</span>
                    </label>
                  </div>
                </div>
                <p className="md:col-span-2 text-xs text-gray-500 -mt-2">Add at least one portfolio link such as Instagram, Facebook, Spotify, or YouTube so admin can verify your work.</p>
                <div className="md:col-span-2">
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white" />
                  <p className="text-xs text-gray-500 mt-2">Upload from your phone or computer, then save changes.</p>
                  {uploadingImage && <p className="text-sm text-[#FF6F00] mt-2">Uploading image...</p>}
                </div>
                <div className="md:col-span-2 rounded-xl border bg-gray-50 p-4"><p className="font-semibold">Public photo gallery</p><p className="mt-1 text-sm text-gray-600">Additional profile photos are managed by KalaManch admin and shown on your public profile. You can still change your main profile photo.</p></div>
                <div className="md:col-span-2 rounded-2xl border border-purple-100 bg-purple-50/60 p-4">
                  <p className="font-semibold text-gray-900">Portfolio videos</p>
                  <p className="mt-1 text-xs text-gray-500">Add up to {MAX_PORTFOLIO_VIDEO_LINKS} YouTube links. Video files are not uploaded, keeping the app faster.</p>
                  <div className="mt-4 grid gap-3">
                    {formData.portfolio_video_urls.map((url, index) => (
                      <input
                        key={`portfolio-video-${index}`}
                        value={url}
                        onChange={(event) => handleSlotChange('portfolio_video_urls', index, event.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-xl bg-white"
                        placeholder={`YouTube video ${index + 1} URL`}
                      />
                    ))}
                  </div>
                </div>
                <div className="md:col-span-2 rounded-3xl border border-emerald-100 bg-gradient-to-br from-emerald-50 via-white to-orange-50 p-5">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    <div>
                      <p className="text-sm font-bold uppercase tracking-[0.22em] text-emerald-700">Upload Quality Assistant</p>
                      <h3 className="mt-2 text-2xl font-bold text-gray-900">{uploadCoach.score}/100</h3>
                      <p className="mt-1 text-sm font-semibold text-gray-700">{uploadCoach.label}</p>
                    </div>
                    <span className={`rounded-full border px-3 py-1 text-xs font-bold ${profileScoreBadgeClass(uploadCoach.tone)}`}>
                      Media readiness
                    </span>
                  </div>
                  <p className="mt-4 text-sm text-gray-600">
                    This checks whether your artist uploads are strong enough for a cleaner public profile and faster collaborator trust.
                  </p>
                  <div className="mt-4 grid gap-3 lg:grid-cols-2">
                    <div className="rounded-2xl bg-white/85 p-4 shadow-sm">
                      <p className="text-sm font-semibold text-gray-900">What looks good</p>
                      <ul className="mt-3 space-y-2 text-sm text-gray-700">
                        {uploadCoach.highlights.map((item) => (
                          <li key={item} className="rounded-xl bg-emerald-50 px-3 py-2">{item}</li>
                        ))}
                      </ul>
                    </div>
                    <div className="rounded-2xl bg-white/85 p-4 shadow-sm">
                      <p className="text-sm font-semibold text-gray-900">Quick improvements</p>
                      <ul className="mt-3 space-y-2 text-sm text-gray-700">
                        {uploadCoach.suggestions.map((item) => (
                          <li key={item} className="rounded-xl bg-orange-50 px-3 py-2">{item}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
                <textarea name="bio" value={formData.bio} onChange={handleChange} rows="5" className="w-full px-4 py-3 border border-gray-300 rounded-xl md:col-span-2" placeholder="Bio" />
                <textarea
                  name="native_bio"
                  value={formData.native_bio}
                  onChange={handleChange}
                  rows="5"
                  className="w-full px-4 py-3 border border-emerald-200 rounded-xl md:col-span-2"
                  placeholder="Native language bio: Hindi, Urdu, Punjabi, Bhojpuri, Bengali, Marathi, or your primary language"
                  dir="auto"
                />
                <div className="md:col-span-2 rounded-3xl border border-emerald-100 bg-emerald-50/70 p-4">
                  <div className="grid gap-3 md:grid-cols-[1fr_auto] md:items-end">
                    <label className="block">
                      <span className="text-sm font-bold text-emerald-900">Auto translate bio</span>
                      <select
                        value={translationLanguage}
                        onChange={(event) => setTranslationLanguage(event.target.value)}
                        className="mt-2 w-full rounded-xl border border-emerald-200 bg-white px-4 py-3 text-gray-900"
                      >
                        {['Hindi', 'Urdu', 'Punjabi', 'Bhojpuri', 'Bengali', 'Marathi', 'Gujarati', 'Tamil', 'Telugu', 'Kannada', 'Malayalam', 'Odia', 'Assamese'].map((language) => (
                          <option key={language} value={language}>{language}</option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      onClick={handleTranslateNativeBio}
                      disabled={translatingBio || !formData.bio.trim()}
                      className="btn-outline"
                    >
                      {translatingBio ? 'Translating...' : 'Translate Bio'}
                    </button>
                  </div>
                  <p className="mt-3 text-xs leading-5 text-emerald-900">
                    AI translation uses your English bio only. Please review spelling, tone, and meaning before saving.
                  </p>
                </div>
                <div className="md:col-span-2 rounded-3xl border border-purple-100 bg-gradient-to-br from-purple-50 via-white to-orange-50 p-4">
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm font-bold text-purple-900">AI Bio Helper</p>
                      <p className="text-xs text-gray-600">Use a draft, then adjust it in your own voice before saving.</p>
                    </div>
                  </div>
                  <div className="mt-3 grid gap-3">
                    {artistBioSuggestions.map((suggestion) => (
                      <div key={suggestion.label} className="rounded-2xl bg-white/85 p-3 shadow-sm">
                        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <p className="text-sm font-semibold text-gray-900">{suggestion.label}</p>
                          <button
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, bio: suggestion.text }))}
                            className="rounded-full border border-purple-200 px-3 py-1 text-xs font-bold text-purple-700 hover:bg-purple-50"
                          >
                            Use this bio
                          </button>
                        </div>
                        <p className="mt-2 text-sm leading-relaxed text-gray-600">{suggestion.text}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="mt-6">
                <button type="submit" className="btn-primary" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</button>
              </div>
            </form>
          </div>
          <div className="space-y-8">
            <div className="bg-white rounded-3xl shadow-lg p-8">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div>
                  <h2 className="text-2xl font-bold gradient-text">AI Profile Coach</h2>
                  <p className="mt-1 text-sm text-gray-500">Improve your profile strength before admin review.</p>
                </div>
                <span className={`rounded-full border px-3 py-1 text-xs font-semibold ${statusClasses}`}>
                  {profileStatus}
                </span>
              </div>
              <div className="mb-5 rounded-3xl border border-orange-100 bg-gradient-to-br from-orange-50 via-white to-purple-50 p-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <p className="text-sm font-semibold text-gray-500">Profile Strength</p>
                    <p className="mt-1 text-4xl font-bold text-gray-900">{profileCoach.score}<span className="text-lg text-gray-500">/100</span></p>
                  </div>
                  <span className={`rounded-full border px-3 py-1 text-xs font-bold ${profileScoreBadgeClass(profileCoach.tone)}`}>
                    {profileCoach.label}
                  </span>
                </div>
                <div className="mt-4 h-3 overflow-hidden rounded-full bg-white shadow-inner">
                  <div
                    className={`h-full rounded-full bg-gradient-to-r ${profileScoreBarClass(profileCoach.tone)}`}
                    style={{ width: `${profileCoach.score}%` }}
                  />
                </div>
                {profileCoach.suggestions.length > 0 && (
                  <div className="mt-4">
                    <p className="text-sm font-bold text-gray-800">Next best improvements</p>
                    <ul className="mt-2 space-y-2 text-sm text-gray-700">
                      {profileCoach.suggestions.map((suggestion) => (
                        <li key={suggestion} className="rounded-2xl bg-white/80 px-3 py-2">
                          {suggestion}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                {checklist.map((item) => (
                  <div key={item.label} className={checklistItemClass(item.complete || item.partial)}>
                    <span>{item.label}</span>
                    <span className="font-bold">{item.complete ? 'Done' : item.partial ? 'Improve' : 'Needed'}</span>
                  </div>
                ))}
              </div>
              <div className="mt-5 space-y-3 text-sm text-gray-700">
                <p><span className="font-semibold">Mobile:</span> {artist.mobile || 'Not added'}</p>
                <p><span className="font-semibold">Artist ID:</span> {artist.id}</p>
                <p><span className="font-semibold">Review:</span> {artist.is_verified ? 'Visible on public artist listings' : 'Pending admin approval. You can still edit and improve your profile.'}</p>
              </div>
            </div>
            <div className="bg-white rounded-3xl shadow-lg p-8">
              <h2 className="text-2xl font-bold gradient-text mb-4">Private Message Board</h2>
              <p className="text-sm text-gray-600 mb-4">Reply to collaborators, discuss bookings, and keep all enquiries on the platform.</p>
              <Link to="/artist/inbox" className="btn-primary w-full text-center block">Open PMB Inbox</Link>
            </div>
            <div className="bg-white rounded-3xl shadow-lg p-8">
              <h2 className="text-2xl font-bold gradient-text mb-4">Change Password</h2>
              <p className="text-sm text-gray-600 mb-2">Update your KalaManch password.</p>
              <p className="text-xs text-gray-500 mb-4">New password must be at least 8 characters.</p>
              {passwordMessage && <div className="mb-4 rounded-xl bg-green-50 border border-green-200 px-4 py-3 text-green-700">{passwordMessage}</div>}
              {passwordError && <div className="mb-4 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-red-700">{passwordError}</div>}
              <form className="space-y-3" onSubmit={handlePasswordChange}>
                <div className="relative">
                  <input
                    type={passwordVisibility.currentPassword ? 'text' : 'password'}
                    value={passwordData.currentPassword}
                    onChange={(event) => setPasswordData((prev) => ({ ...prev, currentPassword: event.target.value }))}
                    className="w-full px-4 py-3 pr-20 border border-gray-300 rounded-xl"
                    placeholder="Current password"
                    required
                  />
                  <PasswordToggleButton visible={passwordVisibility.currentPassword} onClick={() => togglePasswordVisibility('currentPassword')} />
                </div>
                <div className="relative">
                  <input
                    type={passwordVisibility.newPassword ? 'text' : 'password'}
                    value={passwordData.newPassword}
                    onChange={(event) => setPasswordData((prev) => ({ ...prev, newPassword: event.target.value }))}
                    className="w-full px-4 py-3 pr-20 border border-gray-300 rounded-xl"
                    placeholder="New password"
                    minLength={8}
                    required
                  />
                  <PasswordToggleButton visible={passwordVisibility.newPassword} onClick={() => togglePasswordVisibility('newPassword')} />
                </div>
                <div className="relative">
                  <input
                    type={passwordVisibility.confirmPassword ? 'text' : 'password'}
                    value={passwordData.confirmPassword}
                    onChange={(event) => setPasswordData((prev) => ({ ...prev, confirmPassword: event.target.value }))}
                    className="w-full px-4 py-3 pr-20 border border-gray-300 rounded-xl"
                    placeholder="Confirm new password"
                    minLength={8}
                    required
                  />
                  <PasswordToggleButton visible={passwordVisibility.confirmPassword} onClick={() => togglePasswordVisibility('confirmPassword')} />
                </div>
                <button type="submit" className="btn-outline w-full" disabled={passwordSaving}>
                  {passwordSaving ? 'Changing password...' : 'Change Password'}
                </button>
              </form>
            </div>
            <div className="bg-white rounded-3xl shadow-lg p-8">
              <div className="flex items-center justify-between mb-4 gap-3">
                <div>
                  <h2 className="text-2xl font-bold gradient-text">My Videos</h2>
                  <p className="text-sm text-gray-500 mt-1">You can add up to {MAX_VIDEOS} public performance videos.</p>
                </div>
                <button className="btn-outline text-sm px-4 py-2" type="button" onClick={() => setShowVideoForm((prev) => !prev)} disabled={!canAddMoreVideos && !showVideoForm}>
                  {showVideoForm ? 'Close' : canAddMoreVideos ? '+ Add Video' : 'Limit Reached'}
                </button>
              </div>
              {showVideoForm && (
                <div className="mb-6">
                  <VideoSubmissionForm artistId={artist.id} artistName={artist.name} currentVideoCount={videos.length} onVideoAdded={handleVideoAdded} />
                </div>
              )}
              {loadingVideos ? <p className="text-gray-500">Loading videos...</p> : videos.length === 0 ? <p className="text-gray-500">No videos added yet.</p> : (
                <div className="space-y-4">
                  {videos.map((video) => (
                    <div key={video.id} className="rounded-2xl border border-gray-200 p-4">
                      <p className="font-semibold text-gray-800">{video.video_title}</p>
                      {video.description && <p className="text-sm text-gray-600 mt-1">{video.description}</p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyAccount;
